/**
 * Consultas de datos del renderer.
 *
 * Toda clave empieza por la empresa activa: cambiar de consorcio tiene que
 * traer otros datos, nunca reutilizar la caché del anterior. Ese es el mismo
 * criterio del shell (`components/shell/queries.ts`), y no es cosmético: en un
 * ERP multi-empresa, mostrar la caché equivocada es mostrarle a un administrador
 * las expensas de otro edificio.
 */

import { useQueryClient, useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type {
  ListParams,
  ListResponse,
  ModelSpec,
  ReadGroupParams,
  ReadGroupResponse,
  RecordData,
} from '@/lib/types';
import { useSessionStore } from '@/store/session';

export const DATA_KEYS = {
  /** Raíz de todo lo que depende de un modelo: sirve para invalidar de una. */
  model: (companyId: number | null, modelName: string) => ['datos', companyId, modelName] as const,
  list: (companyId: number | null, modelName: string, params: ListParams) =>
    ['datos', companyId, modelName, 'lista', params] as const,
  record: (companyId: number | null, modelName: string, id: number) =>
    ['datos', companyId, modelName, 'registro', id] as const,
  group: (companyId: number | null, modelName: string, params: ReadGroupParams) =>
    ['datos', companyId, modelName, 'grupo', params] as const,
  /**
   * Hilo de seguimiento de un registro (`views/ChatterPanel`).
   *
   * Cuelga de `model` a propósito y no de una raíz propia: el hilo lo escribe
   * el SERVIDOR en cada operación de negocio —confirmar deja una traza, netear
   * una nota de crédito deja otra— así que tiene que caducar con lo mismo que
   * caduca el registro. Con una clave suelta el panel seguía mostrando el hilo
   * de antes de apretar el botón, que es justo lo que el panel viene a arreglar.
   */
  chatter: (companyId: number | null, modelName: string, id: number) =>
    ['datos', companyId, modelName, 'chatter', id] as const,
  /**
   * Prefijo de TODA la metadata de un modelo, con id y sin él. Es el que se
   * invalida: `QUERY_KEYS.meta` incluye el id y solo alcanzaría a una variante.
   */
  metaPrefix: (companyId: number | null, modelName: string) => ['meta', companyId, modelName] as const,
};

export function useCompanyId(): number | null {
  return useSessionStore((estado) => estado.activeCompanyId);
}

/** Listado paginado. `enabled` permite montar la consulta sin dispararla todavía. */
export function useRecordsQuery(
  modelName: string,
  params: ListParams,
  enabled = true,
): UseQueryResult<ListResponse<RecordData>> {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: DATA_KEYS.list(companyId, modelName, params),
    queryFn: ({ signal }) => api.searchRead(modelName, params, signal),
    enabled,
    // Los datos de negocio cambian solos (otro usuario, una tarea de fondo):
    // caducan rápido, al revés que la metadata.
    staleTime: 15_000,
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Los campos que se piden para dibujar una ficha: todos MENOS las relaciones
 * inversas. Cada una le cuesta al servidor una consulta propia —la ficha de un
 * contacto tiene 27— y en un registro ya guardado no las usa nadie: la grilla
 * (`One2ManyField`) pide sus filas por su cuenta apenas hay un id del que colgar.
 *
 * Vive acá y no en el formulario porque lo usan tres lugares que tienen que
 * coincidir en la CLAVE de caché: la lectura de la ficha, el guardado (que deja
 * la respuesta bajo esa misma clave) y el prefetch al abrirla desde la lista.
 */
export function camposDeFicha(spec: Pick<ModelSpec, 'fields'>): string[] {
  return Object.entries(spec.fields ?? {})
    .filter(([, meta]) => meta.type !== 'one2many' && meta.type !== 'many2many')
    .map(([nombre]) => nombre);
}

/** La clave bajo la que vive la ficha de un registro, con sus campos. */
export function claveDeFicha(
  companyId: number | null,
  modelName: string,
  id: number,
  campos: string[],
): readonly unknown[] {
  return [...DATA_KEYS.record(companyId, modelName, id), campos];
}

export function useRecordQuery(
  modelName: string,
  id: number | null,
  /**
   * Qué campos traer. Sin esto el servidor manda TODOS, y en un modelo con
   * muchas relaciones inversas eso es carísimo: cada relación es una consulta
   * propia del lado del servidor.
   *
   * Medido sobre la ficha de un contacto contra la base real: de las 44
   * consultas de esa respuesta, 24 eran las 27 relaciones inversas de
   * `base.partner` —`children`, `invoices`, `sale_orders`, `messages`…—, que
   * juntas producen 515 bytes de JSON. A ~130 ms por ida y vuelta contra la base
   * de producción, más de tres segundos por medio kilobyte.
   *
   * Y no los mira nadie: la grilla de una relación inversa (`One2ManyField`)
   * pide sus propias filas con su propio dominio en cuanto el registro existe;
   * la lista de ids del padre sólo se usa en un ALTA, donde no hay registro que
   * leer. Se comprobó que ningún componente lee esos campos de un registro ya
   * guardado.
   */
  fields?: string[],
): UseQueryResult<RecordData> {
  const companyId = useCompanyId();
  return useQuery({
    // La clave incluye los campos pedidos: dos llamadas que piden cosas
    // distintas no pueden compartir la respuesta.
    queryKey: [...DATA_KEYS.record(companyId, modelName, id ?? 0), fields ?? null],
    queryFn: ({ signal }) => api.readRecord(modelName, id as number, fields, signal),
    enabled: id !== null,
    staleTime: 15_000,
  });
}

export function useReadGroupQuery(
  modelName: string,
  params: ReadGroupParams,
  enabled = true,
): UseQueryResult<ReadGroupResponse> {
  const companyId = useCompanyId();
  return useQuery({
    queryKey: DATA_KEYS.group(companyId, modelName, params),
    queryFn: ({ signal }) => api.readGroup(modelName, params, signal),
    enabled,
    staleTime: 15_000,
  });
}

/**
 * Invalida todo lo cacheado de un modelo: datos Y metadata por registro.
 *
 * Después de escribir se invalida el modelo entero y no solo la consulta que se
 * tocó: un cambio de estado mueve la fila de columna en el kanban, altera los
 * conteos de los grupos y puede sacarla del filtro activo de la lista. Refrescar
 * de menos deja pantallas que se contradicen entre sí.
 *
 * También cae la metadata, porque el `enabled` de las acciones se evalúa contra
 * el estado del registro: confirmar una factura tiene que apagar «Confirmar» y
 * encender «Anular» sin recargar la página. Sin esto, la botonera quedaría
 * describiendo el estado anterior al botón que el usuario acaba de apretar.
 */
/**
 * Invalida SÓLO las variantes con `?id=` de la metadata de un modelo.
 *
 * La variante sin id —campos, vistas, permisos— no cambia por guardar un
 * registro: cambia con un deploy o al mover permisos, y por eso se cachea diez
 * minutos. Invalidarla también (lo que hacía `metaPrefix`) obligaba a bajar de
 * nuevo 60-150 KB de metadata en el pedido siguiente a CADA guardado, medio
 * segundo de costo fijo por pedido incluido. Lo que sí depende del estado del
 * registro es el `enabled` de la botonera, que vive en las variantes con id.
 */
function invalidarMetaDeLosRegistros(
  cliente: QueryClient,
  companyId: number | null,
  modelName: string,
): Promise<void> {
  return cliente.invalidateQueries({
    predicate: (consulta) => {
      const clave = consulta.queryKey;
      return (
        clave[0] === 'meta' &&
        clave[1] === companyId &&
        clave[2] === modelName &&
        clave[3] !== null &&
        clave[3] !== undefined
      );
    },
  });
}

export function useInvalidateModel(): (modelName: string, exceptoRegistro?: number) => Promise<void> {
  const cliente = useQueryClient();
  const companyId = useCompanyId();
  return async (modelName: string, exceptoRegistro?: number) => {
    await Promise.all([
      // `exceptoRegistro`: la ficha que se acaba de guardar NO se vuelve a
      // pedir. La respuesta del guardado ya es esa ficha —releída por el
      // servidor con el queryset de lectura y con sus contadores— y quedó en la
      // caché bajo su clave. Volver a pedirla era un pedido entero de más
      // después de cada «Guardar». Lo demás del modelo (listas, agrupados, el
      // hilo del registro) sí cae, porque eso sí puede haber cambiado.
      exceptoRegistro === undefined
        ? cliente.invalidateQueries({ queryKey: DATA_KEYS.model(companyId, modelName) })
        : cliente.invalidateQueries({
            queryKey: DATA_KEYS.model(companyId, modelName),
            predicate: (consulta) =>
              !(consulta.queryKey[3] === 'registro' && consulta.queryKey[4] === exceptoRegistro),
          }),
      // Las de todos los registros; la del modelo no cambia por guardar.
      invalidarMetaDeLosRegistros(cliente, companyId, modelName),
    ]);
  };
}

/**
 * Invalida los datos de la empresa activa ENTEROS, y la metadata de un modelo.
 *
 * Es para las **acciones de servidor**, que es lo único que escribe en modelos
 * que la pantalla no nombra. `useInvalidateModel` alcanza cuando se guarda un
 * registro —lo que cambia es ese modelo—, pero una acción es una operación de
 * negocio: «Importar el resumen» escribe las líneas del extracto, «Confirmar»
 * una factura escribe el asiento y sus apuntes, «Liquidar» escribe los cargos
 * por unidad. Ninguno de esos modelos es el del botón, así que la grilla
 * embebida seguía mostrando la respuesta anterior —«Todavía no se importó
 * ningún movimiento» al lado de un encabezado que ya decía 8— hasta recargar la
 * página a mano.
 *
 * Qué NO se invalida y por qué: la metadata de los demás modelos. Es cara, casi
 * nunca cambia por una acción y la del modelo accionado —que es la que lleva el
 * `enabled` de la botonera— sí cae. React Query sólo vuelve a pedir lo que está
 * montado, así que invalidar de más cuesta las consultas de la pantalla abierta
 * y nada más.
 */
export function useInvalidateData(): (modelName: string) => Promise<void> {
  const cliente = useQueryClient();
  const companyId = useCompanyId();
  return async (modelName: string) => {
    await Promise.all([
      cliente.invalidateQueries({ queryKey: ['datos', companyId] }),
      invalidarMetaDeLosRegistros(cliente, companyId, modelName),
    ]);
  };
}
